/**
 * Optimized Storage Hook - TypeScript
 * Portal ICAVI - Sprint 4 Migration
 * Hook para otimizar operações de localStorage com debounce e batch operations
 */
import { useCallback, useRef, useEffect } from 'react';
import { useIdleCallback } from './useIdleCallback';
// ============================================================================
// Main Hook
// ============================================================================
/**
 * Hook para otimizar operações de localStorage com debounce e batch operations
 * Reduz a frequência de escritas no localStorage e agrupa operações para melhor performance
 *
 * @returns Objeto com funções otimizadas de localStorage
 *
 * @example
 * ```tsx
 * const storage = useOptimizedStorage();
 *
 * // Salvar com debounce
 * storage.setItem('user', { name: 'John' });
 *
 * // Salvar imediatamente
 * storage.setItem('critical', data, true);
 *
 * // Obter valor
 * const user = storage.getItem('user');
 * ```
 */
export const useOptimizedStorage = () => {
    const batchOperations = useRef(new Map());
    const pendingOperations = useRef(new Map());
    const debounceTimers = useRef(new Map());
    const { scheduleIdleCacheOperation, scheduleIdleCleanup } = useIdleCallback();
    const DEBOUNCE_DELAY = 300; // 300ms de delay para debounce
    const BATCH_DELAY = 100; // 100ms para agrupar operações em batch
    /**
     * Limpa todos os timers e executa operações pendentes ao desmontar o componente
     */
    useEffect(() => {
        return () => {
            // Limpar timers
            debounceTimers.current.forEach(timer => clearTimeout(timer));
            debounceTimers.current.clear();
            // Executar operações pendentes (debounce)
            pendingOperations.current.forEach((operation, key) => {
                try {
                    if (operation.type === 'set' && operation.value) {
                        localStorage.setItem(key, operation.value);
                    }
                    else if (operation.type === 'remove') {
                        localStorage.removeItem(key);
                    }
                }
                catch (error) {
                    console.warn(`Erro ao salvar dados pendentes no localStorage (chave: '${key}'):`, error);
                }
            });
            pendingOperations.current.clear();
            // Executar operações em batch pendentes
            batchOperations.current.forEach((operation, key) => {
                try {
                    if (operation.type === 'set' && operation.value) {
                        localStorage.setItem(key, operation.value);
                    }
                    else if (operation.type === 'remove') {
                        localStorage.removeItem(key);
                    }
                }
                catch (error) {
                    console.warn(`Erro ao executar batch pendente no localStorage (chave: '${key}'):`, error);
                }
            });
            batchOperations.current.clear();
        };
    }, []);
    /**
     * Executa operações em batch para melhor performance
     */
    const executeBatchOperations = useCallback(() => {
        if (batchOperations.current.size === 0)
            return;
        try {
            // Executa todas as operações em batch
            batchOperations.current.forEach((operation, key) => {
                try {
                    if (operation.type === 'set' && operation.value) {
                        localStorage.setItem(key, operation.value);
                    }
                    else if (operation.type === 'remove') {
                        localStorage.removeItem(key);
                    }
                }
                catch (error) {
                    console.warn(`Erro ao executar operação localStorage para chave '${key}':`, error);
                }
            });
            // Limpa o batch após execução
            batchOperations.current.clear();
        }
        catch (error) {
            console.error('Erro ao executar batch operations do localStorage:', error);
        }
    }, []);
    /**
     * Adiciona operação ao batch com delay
     */
    const addToBatch = useCallback((key, operation) => {
        batchOperations.current.set(key, operation);
        // Agenda execução do batch durante período de inatividade para operações não críticas
        if (operation.type === 'set' && !operation.immediate) {
            scheduleIdleCacheOperation(executeBatchOperations);
        }
        else {
            setTimeout(executeBatchOperations, BATCH_DELAY);
        }
    }, [executeBatchOperations, scheduleIdleCacheOperation]);
    /**
     * Salva item no localStorage com debounce
     * @param key Chave do localStorage
     * @param value Valor a ser salvo (será serializado como JSON)
     * @param immediate Se true, salva imediatamente sem debounce
     */
    const setItem = useCallback((key, value, immediate = false) => {
        const serializedValue = typeof value === 'string' ? value : JSON.stringify(value);
        if (immediate) {
            try {
                localStorage.setItem(key, serializedValue);
                // Remover de pendentes se existir, pois já foi salvo
                pendingOperations.current.delete(key);
                batchOperations.current.delete(key);
            }
            catch (error) {
                console.warn(`Erro ao salvar no localStorage (chave: '${key}'):`, error);
            }
            return;
        }
        // Adicionar aos pendentes para garantir salvamento no unmount
        pendingOperations.current.set(key, { type: 'set', value: serializedValue });
        // Cancela timer anterior se existir
        if (debounceTimers.current.has(key)) {
            clearTimeout(debounceTimers.current.get(key));
        }
        // Cria novo timer com debounce
        const timer = setTimeout(() => {
            addToBatch(key, { type: 'set', value: serializedValue });
            pendingOperations.current.delete(key); // Remove de pendentes pois foi para batch
            debounceTimers.current.delete(key);
        }, DEBOUNCE_DELAY);
        debounceTimers.current.set(key, timer);
    }, [addToBatch]);
    /**
     * Remove item do localStorage com debounce
     * @param key Chave do localStorage
     * @param immediate Se true, remove imediatamente sem debounce
     */
    const removeItem = useCallback((key, immediate = false) => {
        if (immediate) {
            try {
                localStorage.removeItem(key);
                // Remover de pendentes se existir
                pendingOperations.current.delete(key);
                batchOperations.current.delete(key);
            }
            catch (error) {
                console.warn(`Erro ao remover do localStorage (chave: '${key}'):`, error);
            }
            return;
        }
        // Adicionar aos pendentes
        pendingOperations.current.set(key, { type: 'remove' });
        // Cancela timer anterior se existir
        if (debounceTimers.current.has(key)) {
            clearTimeout(debounceTimers.current.get(key));
        }
        // Cria novo timer com debounce
        const timer = setTimeout(() => {
            addToBatch(key, { type: 'remove' });
            pendingOperations.current.delete(key); // Remove de pendentes pois foi para batch
            debounceTimers.current.delete(key);
        }, DEBOUNCE_DELAY);
        debounceTimers.current.set(key, timer);
    }, [addToBatch]);
    /**
     * Obtém item do localStorage (operação síncrona)
     * @param key Chave do localStorage
     * @param defaultValue Valor padrão se a chave não existir
     * @returns Valor deserializado ou defaultValue
     */
    const getItem = useCallback((key, defaultValue = null) => {
        try {
            const item = localStorage.getItem(key);
            if (item === null)
                return defaultValue;
            // Tenta fazer parse JSON, se falhar retorna string
            try {
                return JSON.parse(item);
            }
            catch {
                return item;
            }
        }
        catch (error) {
            console.warn(`Erro ao ler do localStorage (chave: '${key}'):`, error);
            return defaultValue;
        }
    }, []);
    /**
     * Remove múltiplas chaves em batch
     * @param keys Array de chaves para remover
     * @param immediate Se true, remove imediatamente
     */
    const removeMultiple = useCallback((keys, immediate = false) => {
        if (immediate) {
            keys.forEach(key => {
                try {
                    localStorage.removeItem(key);
                }
                catch (error) {
                    console.warn(`Erro ao remover do localStorage (chave: '${key}'):`, error);
                }
            });
            return;
        }
        keys.forEach(key => removeItem(key, false));
    }, [removeItem]);
    /**
     * Salva múltiplos itens em batch
     * @param items Objeto com chave-valor para salvar
     * @param immediate Se true, salva imediatamente
     */
    const setMultiple = useCallback((items, immediate = false) => {
        Object.entries(items).forEach(([key, value]) => {
            setItem(key, value, immediate);
        });
    }, [setItem]);
    /**
     * Força execução imediata de todas as operações pendentes
     */
    const flush = useCallback(() => {
        // Cancela todos os timers de debounce
        debounceTimers.current.forEach(timer => clearTimeout(timer));
        debounceTimers.current.clear();
        // Executa operações pendentes
        executeBatchOperations();
    }, [executeBatchOperations]);
    /**
     * Limpa cache específico por padrão de chave durante período de inatividade
     * @param pattern Padrão para filtrar chaves (ex: 'cache_')
     * @param immediate Se true, executa imediatamente
     * @returns Número de chaves removidas
     */
    const clearCacheByPattern = useCallback((pattern, immediate = false) => {
        const cleanupOperation = () => {
            try {
                const keysToRemove = [];
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key && key.includes(pattern)) {
                        keysToRemove.push(key);
                    }
                }
                removeMultiple(keysToRemove, true);
                return keysToRemove.length;
            }
            catch (error) {
                console.warn(`Erro ao limpar cache por padrão '${pattern}':`, error);
                return 0;
            }
        };
        if (immediate) {
            return cleanupOperation();
        }
        else {
            // Agendar para período de inatividade
            scheduleIdleCleanup(cleanupOperation);
            return 0; // Retorna 0 pois será executado depois
        }
    }, [removeMultiple, scheduleIdleCleanup]);
    return {
        setItem,
        getItem,
        removeItem,
        removeMultiple,
        setMultiple,
        flush,
        clearCacheByPattern,
    };
};
// ============================================================================
// Cache Hook with TTL
// ============================================================================
/**
 * Hook específico para cache com TTL (Time To Live)
 * Gerencia cache com expiração automática
 *
 * @returns Objeto com funções de cache com TTL
 *
 * @example
 * ```tsx
 * const cache = useOptimizedCache();
 *
 * // Salvar no cache com TTL de 10 minutos
 * cache.setCache('userData', user, 10);
 *
 * // Obter do cache (retorna null se expirado)
 * const user = cache.getCache<User>('userData');
 *
 * // Limpar cache expirado
 * cache.clearExpiredCache();
 * ```
 */
export const useOptimizedCache = () => {
    const storage = useOptimizedStorage();
    const { scheduleIdleCleanup } = useIdleCallback();
    /**
     * Salva item no cache com TTL
     * @param key Chave do cache
     * @param data Dados para cachear
     * @param ttlMinutes TTL em minutos (padrão: 10)
     */
    const setCache = useCallback((key, data, ttlMinutes = 10) => {
        const cacheData = {
            data,
            timestamp: Date.now(),
            ttl: ttlMinutes * 60 * 1000, // Converte para millisegundos
        };
        storage.setItem(`cache_${key}`, cacheData);
    }, [storage]);
    /**
     * Obtém item do cache verificando TTL
     * @param key Chave do cache
     * @returns Dados do cache ou null se expirado/inexistente
     */
    const getCache = useCallback((key) => {
        const cached = storage.getItem(`cache_${key}`);
        if (!cached || !cached.timestamp || !cached.ttl) {
            return null;
        }
        const now = Date.now();
        const isExpired = now - cached.timestamp > cached.ttl;
        if (isExpired) {
            storage.removeItem(`cache_${key}`, true);
            return null;
        }
        return cached.data;
    }, [storage]);
    /**
     * Remove item específico do cache
     * @param key Chave do cache
     */
    const removeCache = useCallback((key) => {
        storage.removeItem(`cache_${key}`, true);
    }, [storage]);
    /**
     * Limpa todo o cache
     */
    const clearCache = useCallback(() => {
        storage.clearCacheByPattern('cache_');
    }, [storage]);
    /**
     * Limpa cache expirado
     * @param immediate Se true, executa imediatamente
     * @returns Número de chaves removidas
     */
    const clearExpiredCache = useCallback((immediate = false) => {
        const cleanupOperation = () => {
            try {
                const keysToRemove = [];
                const now = Date.now();
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key && key.startsWith('cache_')) {
                        const cached = storage.getItem(key);
                        if (cached && cached.timestamp && cached.ttl) {
                            const isExpired = now - cached.timestamp > cached.ttl;
                            if (isExpired) {
                                keysToRemove.push(key);
                            }
                        }
                    }
                }
                storage.removeMultiple(keysToRemove, true);
                return keysToRemove.length;
            }
            catch (error) {
                console.warn('Erro ao limpar cache expirado:', error);
                return 0;
            }
        };
        if (immediate) {
            return cleanupOperation();
        }
        else {
            // Agendar limpeza para período de inatividade
            scheduleIdleCleanup(cleanupOperation);
            return 0; // Retorna 0 pois será executado depois
        }
    }, [storage, scheduleIdleCleanup]);
    return {
        setCache,
        getCache,
        removeCache,
        clearCache,
        clearExpiredCache,
    };
};
/**
 * Export default para compatibilidade
 */
export default useOptimizedStorage;
